import { useEffect, useState } from "react";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Button,
	Box,
	Chip,
	Typography,
	Alert,
	CircularProgress,
} from "@mui/material";
import dayjs from "dayjs";
import DatagridComponent from "../../../datagridComponent";
import { getExecucoesAutomacao } from "../api";

const COR_STATUS = {
	SUCESSO: "success",
	ERRO: "error",
	PENDENTE: "default",
};

// Resumo de uma linha do histórico a partir do JSON `resultado` gravado pelo
// motor (ticket criado, chamada externa ou erro).
function resumoResultado(execucao) {
	const r = execucao?.resultado;
	if (!r) return "—";
	if (r.erro) {
		// O motor grava etapa/httpStatus/respostaErp; a mensagem crua do axios
		// ("Request failed with status code 500") não diz onde nem por quê.
		const partes = [];
		if (r.etapa) partes.push(`Falha ao ${r.etapa}`);
		if (r.httpStatus) partes.push(`HTTP ${r.httpStatus}`);
		if (r.respostaErp && r.respostaErp !== "(vazio)") {
			partes.push(String(r.respostaErp).slice(0, 120));
		} else if (r.httpStatus >= 500) {
			partes.push("sem resposta do servidor");
		}
		return partes.length ? partes.join(" — ") : r.erro;
	}
	if (r.acao === "CRIAR_TICKET") {
		const idTicket =
			r.ticket?.id_atendimento || r.ticket?.id || r.ticket?.protocolo;
		return idTicket ? `Ticket ${idTicket}` : "Ticket criado";
	}
	if (r.acao === "CHAMAR_API") {
		return `${r.metodo || ""} ${r.url || ""} → HTTP ${r.status ?? "?"}`.trim();
	}
	return "—";
}

// Histórico de disparos de uma automação. Clicar numa linha mostra o JSON
// completo do resultado (payload do ticket / resposta da API / erro).
export default function ModalExecucoes({ open, onClose, automacao, allHosts = [] }) {
	const [carregando, setCarregando] = useState(false);
	const [erro, setErro] = useState("");
	const [execucoes, setExecucoes] = useState([]);
	const [selecionada, setSelecionada] = useState(null);

	useEffect(() => {
		if (!open || !automacao?.id) return;
		let ativo = true;
		(async () => {
			try {
				setCarregando(true);
				setErro("");
				setSelecionada(null);
				const dados = await getExecucoesAutomacao(automacao.id);
				if (ativo) setExecucoes(dados?.execucoes || []);
			} catch (e) {
				if (ativo)
					setErro(
						e?.response?.data?.message || "Falha ao carregar as execuções."
					);
			} finally {
				if (ativo) setCarregando(false);
			}
		})();
		return () => {
			ativo = false;
		};
	}, [open, automacao?.id]);

	const colunas = [
		{
			field: "disparadoEm",
			headerName: "Disparado em",
			width: 150,
			// x-data-grid v7: valueGetter é (value, row), não (params)
			valueGetter: (value) => dayjs(value).format("DD/MM/YYYY HH:mm"),
		},
		{
			field: "status",
			headerName: "Status",
			width: 110,
			renderCell: (p) => (
				<Chip
					size="small"
					color={COR_STATUS[p.value] || "default"}
					label={p.value}
				/>
			),
		},
		{
			field: "hostId",
			headerName: "Host",
			flex: 1,
			minWidth: 150,
			// /zabbix/hostList devolve `host`, não `name`.
			valueGetter: (value) => {
				const h = allHosts.find((x) => String(x?.hostid) === String(value));
				return h?.host || h?.name || value;
			},
		},
		{
			field: "resumo",
			headerName: "Resultado",
			flex: 1.4,
			minWidth: 200,
			valueGetter: (_, row) => resumoResultado(row),
		},
	];

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>
				Execuções — {automacao?.nome || ""}
				<Typography variant="body2" color="text.secondary">
					Últimos disparos desta automação (máx. 100). Clique numa linha para
					ver o detalhe.
				</Typography>
			</DialogTitle>
			<DialogContent>
				{erro && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{erro}
					</Alert>
				)}
				{carregando ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
						<CircularProgress size={28} />
					</Box>
				) : execucoes.length === 0 && !erro ? (
					<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
						Esta automação ainda não disparou nenhuma vez.
					</Typography>
				) : (
					<>
						<Box sx={{ height: 320, width: "100%" }}>
							<DatagridComponent
								rows={execucoes}
								columns={colunas}
								loading={carregando}
								isPagination
								setSelectedRow={setSelecionada}
							/>
						</Box>
						{selecionada && (
							<Box
								component="pre"
								sx={{
									mt: 2,
									p: 1.5,
									bgcolor: "action.hover",
									borderRadius: 1,
									fontSize: 12,
									maxHeight: 220,
									overflow: "auto",
									whiteSpace: "pre-wrap",
								}}
							>
								{JSON.stringify(
									{
										eventId: selecionada.eventId,
										status: selecionada.status,
										resultado: selecionada.resultado,
									},
									null,
									2
								)}
							</Box>
						)}
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Fechar</Button>
			</DialogActions>
		</Dialog>
	);
}
